import { useContext, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "react-router";
import { Bento } from "@/components/ui/box";
import { BentoVisit } from "@/components/ui/box-visit-context";
import { searchWithTrail } from "@/lib/visit/address";
import {
  selectionOf,
  type Box,
  type ListBox,
  type TaskBox,
  type Result,
  type FieldSave,
  type FieldAria,
  type Blocker,
} from "@/components/ui/box.schema";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ConflictNotice } from "@/components/ui/conflict-notice";
import { ApiError } from "@/lib/api/client";
import {
  gradebookApi,
  type GradeItem,
  type GradeRow,
  type GradesGrid,
  type SubmitFinalResult,
} from "@/lib/api/gradebook";
import { outcomesApi } from "@/lib/api/outcomes";
import { gradeScaleApi } from "@/lib/api/gradeScale";
import { currentOnConflict } from "@/lib/api/conflict";
import type { BulkResult } from "@/lib/api/commitSeams";
import {
  registerCopy,
  registerRead,
  registerCommit,
  recorded,
  bulkResult,
  keepResult,
  registerText,
  registerPhrases,
  registerCandidate,
} from "@/lib/register-copy";
import { letter, weighted } from "@/lib/grades";
import { useAssessmentBoxes } from "./use-assessment-boxes";
import { useOutcomeBox } from "./use-outcome-box";
import { courseWorkspacePath, registrarCoursePath } from "@/navigation/routes";

interface Props {
  courseId: string;
  mode: "instructor" | "admin";
  focusEnrollmentId?: string | null;
}
const serverScore = (row: GradeRow, item: GradeItem) =>
  row.scores[item.id] ?? (item.key ? row.scores[item.key] : null) ?? null;
const scoreText = (value: number | null) =>
  value === null ? "" : String(value);
const noLetter = (value: string | null | undefined) =>
  !value || value === "\u2014" || value === "-";
type ScoreWrite = (
  scores: Parameters<typeof gradebookApi.postGrades>[1],
  write: NonNullable<Parameters<typeof gradebookApi.postGrades>[2]>,
) => ReturnType<typeof gradebookApi.postGrades>;

function ScoreCell({
  courseId,
  row,
  item,
  version,
  writeScores,
  refresh,
  onDraft,
  onSave,
  labelledBy,
  aria,
  lastRow,
}: {
  courseId: string;
  row: GradeRow;
  item: GradeItem;
  version?: string;
  writeScores: ScoreWrite;
  refresh: () => Promise<void>;
  onDraft: (value: string | undefined) => void;
  onSave: (save: FieldSave) => void;
  labelledBy: string;
  aria: FieldAria;
  lastRow: boolean;
}) {
  const saved = serverScore(row, item);
  const [value, setValue] = useState(() => scoreText(saved));
  const [save, storeSave] = useState<FieldSave>({ status: "idle" });
  const setSave = (next: FieldSave) => {
    storeSave(next);
    onSave(next);
  };
  const [unknown, setUnknown] = useState<Result | null>(null);
  const [conflict, setConflict] = useState<{
    value: number | null;
    version: string;
  } | null>(null);
  const base = useRef({ value: saved, version });
  const inFlight = useRef(false);
  useEffect(() => {
    if (value === scoreText(base.current.value) && !inFlight.current &&
      (!base.current.version || version === base.current.version || Date.parse(version ?? "") > Date.parse(base.current.version))) {
      base.current = { value: saved, version };
      setValue(scoreText(saved));
    }
  }, [saved, version, value]);
  const send = async () => {
    if (
      inFlight.current ||
      conflict ||
      unknown ||
      value === scoreText(base.current.value)
    )
      return;
    const number = value.trim() ? Number(value) : null;
    if (
      number !== null &&
      (!Number.isFinite(number) || number < 0 || number > 100)
    ) {
      setSave({ status: "refused", sentence: registerCopy.scoreLimit });
      return;
    }
    if (!base.current.version) {
      setSave({
        status: "refused",
        sentence: registerText.readTheLatestScoresBeforeSaving,
      });
      return;
    }
    const key = crypto.randomUUID();
    inFlight.current = true;
    setSave({ status: "saving" });
    const result = await registerCommit(
      key,
      async () => {
        try {
          return await writeScores(
            [
              {
                enrollment_id: row.enrollment_id,
                grade_item_id: item.id,
                score: number,
              },
            ],
            { key, version: base.current.version! },
          );
        } catch (error) {
          if (currentOnConflict(error)) {
            const newer = await gradebookApi.getGrades(courseId);
            const newerRow = newer.rows.find(
              (candidate) => candidate.enrollment_id === row.enrollment_id,
            );
            if (newerRow && newer.updated_at)
              setConflict({
                value: serverScore(newerRow, item),
                version: newer.updated_at,
              });
          }
          throw error;
        }
      },
      async (answer) => {
        base.current = { value: number, version: answer?.updated_at ?? base.current.version };
        await refresh();
        onDraft(undefined);
        return recorded([registerText.scoreSaved]);
      },
    );
    inFlight.current = false;
    if (result.state === "confirmed") setSave({ status: "saved" });
    else if (result.state === "unknown") {
      setUnknown(result);
      setSave({ status: "unknown", check: async () => {
        const next = await result.check.run();
        if (next.state === "confirmed") { setUnknown(null); setSave({ status: "saved" }); }
        else if (next.state === "refused") { setUnknown(null); setSave({ status: "refused", sentence: next.sentence }); }
      } });
    } else if (result.state === "refused")
      setSave({ status: "refused", sentence: result.sentence });
  };
  // The cell's save word is local to its value, including an uncertain response.
  return (
    <div className="grid min-w-24 gap-1" data-cell-save={save.status}>
      <Input
        aria-labelledby={labelledBy}
        {...aria}
        inputMode="decimal"
        disabled={unknown?.state === "unknown"}
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
          onDraft(event.target.value);
          if (!unknown) setSave({ status: "idle" });
        }}
        onBlur={() => void send()}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            setValue(scoreText(base.current.value));
            onDraft(undefined);
            setSave({ status: "idle" });
          } else if (event.key === "Enter" && lastRow && !event.shiftKey) {
            event.preventDefault();
            event.stopPropagation();
            void send();
          }
        }}
        className="w-24 font-data tabular-nums"
      />
      {conflict && (
        <div className="grid gap-1 text-meta">
          <p>
            {registerText.newerScore}{conflict.value ?? registerText.notRecorded}{registerText.yourScore}{" "}
            {value || "Cleared"}.
          </p>
          <button
            type="button"
            onClick={() => {
              base.current = {
                value: conflict.value,
                version: conflict.version,
              };
              setConflict(null);
            }}
          >
            {registerText.useMyScoreOnTheNewerVersion}</button>
          <button
            type="button"
            onClick={() => {
              base.current = {
                value: conflict.value,
                version: conflict.version,
              };
              setValue(scoreText(conflict.value));
              onDraft(undefined);
              setConflict(null);
              setSave({ status: "idle" });
            }}
          >
            {registerText.loadNewerScore}</button>
        </div>
      )}

    </div>
  );
}

export function GradebookGrid(props: Props) {
  return <GradebookForCourse key={props.courseId} {...props} />;
}
function GradebookForCourse({ courseId, mode, focusEnrollmentId }: Props) {
  const queries = useQueryClient();
  const scoreQueue = useRef<Promise<unknown>>(Promise.resolve());
  const ownVersions = useRef(new Map<string, string>());
  const writeScores: ScoreWrite = (scores, write) => {
    const next = scoreQueue.current.catch(() => undefined).then(async () => {
      let version = write.version;
      // Only a confirmed write from this mounted course can advance a dirty
      // cell's version. A read or another writer never rebases it silently.
      while (ownVersions.current.has(version)) version = ownVersions.current.get(version)!;
      const answer = await gradebookApi.postGrades(courseId, scores, { ...write, version });
      if (answer?.updated_at && answer.updated_at !== version)
        ownVersions.current.set(version, answer.updated_at);
      return answer;
    });
    scoreQueue.current = next;
    return next;
  };
  const { pathname, search } = useLocation();
  const visitHost = useContext(BentoVisit);
  const itemsQuery = useQuery({
    queryKey: ["gradebook", "items", courseId],
    queryFn: () => gradebookApi.getGradeItems(courseId),
  });
  const grades = useQuery({
    queryKey: ["gradebook", "grades", courseId],
    queryFn: () => gradebookApi.getGrades(courseId),
  });
  const ranks = useQuery({
    queryKey: ["register-ranks", courseId],
    queryFn: () => gradebookApi.getRanks(courseId),
  });
  const scale = useQuery({
    queryKey: ["grade-scale"],
    queryFn: () => gradeScaleApi.getGradeScale(),
  });
  const outcomes = useQuery({
    queryKey: ["outcomes", "course", courseId],
    queryFn: () => outcomesApi.getCourse(courseId),
  });
  const [cellSaves, setCellSaves] = useState<Record<string, FieldSave>>({});
  const [scoreDrafts, setScoreDrafts] = useState<
    Record<string, Record<string, string>>
  >({});
  const [submitResult, setSubmitResult] = useState<Result>({ state: "none" });
  const [basisEditing, setBasisEditing] = useState(false);
  const [overrides, setOverrides] = useState<
    Record<string, { score: string; letter: string; reason: string; version?: string }>
  >({});
  const [overrideResults, setOverrideResults] = useState<
    Record<string, Result>
  >({});
  const items = itemsQuery.data ?? [];
  const rows = grades.data?.rows ?? [];
  const frozen =
    outcomes.data?.outcomes.some(
      (row) => row.status !== "final_grade_not_submitted",
    ) || grades.data?.locked;
  const frozenReason = frozen
    ? registerText.aSubmittedOrCertifiedResultStandsOnTheseAssessmentLines
    : null;
  const refresh = async () => {
    await queries.cancelQueries({ queryKey: ["gradebook", "items", courseId] });
    await Promise.all([
      queries.invalidateQueries({
        queryKey: ["gradebook", "grades", courseId],
      }),
      queries.invalidateQueries({ queryKey: ["gradebook", "items", courseId] }),
      queries.invalidateQueries({ queryKey: ["outcomes", "course", courseId] }),
      queries.invalidateQueries({ queryKey: ["register-ranks", courseId] }),
    ]);
  };
  const { boxes: lineBoxes, onOpenChange: onLineOpenChange } = useAssessmentBoxes({
    courseId,
    items,
    read: registerRead(itemsQuery, registerText.assessmentLines),
    version: grades.data?.updated_at,
    scoredCounts: Object.fromEntries(
      items.map((item) => [
        item.id,
        rows.filter((row) => serverScore(row, item) !== null).length,
      ]),
    ),
    frozenReason,
    onChanged: refresh,
    onEditingChange: setBasisEditing,
  });
  const outcomeBox = useOutcomeBox({
    mode,
    projection: outcomes.data,
    read: registerRead(outcomes, registerText.courseOutcomes),
    onRefresh: refresh,
    focusEnrollmentId,
  });
  const workPath = (id: string) =>
    `${pathname.startsWith("/admin/teaching") ? courseWorkspacePath("adminTeaching", courseId, "coursework") : mode === "admin" ? registrarCoursePath(courseId, "coursework") : courseWorkspacePath("instructor", courseId, "coursework")}&assignment=${encodeURIComponent(id)}`;
  const weight = items.reduce((sum, item) => sum + item.weight, 0);
  const credit = rows.filter(
    (row) => row.participation_kind !== "audit" && !row.final_locked && !row.certified,
  );
  const scores: ListBox<GradesGrid, GradeRow> = {
    kind: "list",
    id: "grade-scores",
    label: registerText.scoresByAssessment,
    job: registerText.readAndRecordProvisionalAssessmentScores,
    owner: { system: "imam" },
    fold: "never",
    read: registerRead(grades, registerText.scores),
    detail: () => "",
    state: (value) => {
      // Submission is said once, by Submit final grades; locked, the scores only close (#1416).
      if (value.locked) return { class: "done", word: registerText.scoresClosed };
      // The job's own word: how many scores are still to record, never a mode.
      const open = value.rows
        .filter((row) => row.participation_kind !== "audit" && !row.final_locked && !row.certified)
        .reduce((sum, row) => sum + items.filter((item) => serverScore(row, item) === null).length, 0);
      if (!items.length || !value.rows.length) return { class: "quiet", word: registerText.noScoresToRecord };
      return open
        ? { class: "waiting", word: registerPhrases.scoresToRecord(open) }
        : { class: "done", word: registerText.allScoresRecorded };
    },
    rows: (value) => value.rows,
    table: {
      title: registerText.learner,
      note: `${registerCopy.provisional}${scale.data && !scale.data.ratified ? ` ${registerCopy.advisory}` : ""}`,
      columns: [
        ...items.map((item) => ({
          id: item.id,
          label: item.label,
          // The weight is the assessment line's fact; the column names only its source.
          sub: grades.data?.grade_items?.find((line) => line.id === item.id)?.linked ? registerText.fromCoursework : undefined,
        })),
        { id: "percentage", label: registerText.course },
        { id: "letter", label: registerText.letter },
      ],
    },
    candidate: { eligible: mode === "admin", blocked: null, rank: null },
    row: {
      key: (row) => row.enrollment_id,
      title: (row) => row.display_name,
      facts: (row) => (row.participation_kind === "audit" ? registerText.auditor : ""),
      state: () => null,
      cells: (row: GradeRow) => {
        const local = scoreDrafts[row.enrollment_id];
        const values = Object.fromEntries(
          items.map((item) => [
            item.id,
            local?.[item.id] === undefined
              ? serverScore(row, item)
              : local[item.id].trim()
                ? Number(local[item.id])
                : null,
          ]),
        );
        const valid = Object.values(values).every(
          (value) =>
            value === null ||
            (Number.isFinite(value) && value >= 0 && value <= 100),
        );
        const percentage = valid ? weighted(items, values) : null;
        const gradeLetter =
          percentage !== null && scale.data
            ? letter(percentage, scale.data.bands)
            : "";
        return [
          ...items.map((item) => {
            const linked =
              grades.data?.grade_items?.find((line) => line.id === item.id)
                ?.linked_assignments ?? [];
            if (grades.data?.locked || linked.length)
              return {
                id: item.id,
                read: String(serverScore(row, item) ?? registerText.notRecorded),
              };
            return {
              id: item.id,
              render: (labelledBy: string, aria: FieldAria) => (
                <ScoreCell
                  writeScores={writeScores}
                  labelledBy={labelledBy}
                  aria={aria}
                  lastRow={
                    row.enrollment_id === rows[rows.length - 1]?.enrollment_id
                  }
                  onSave={(value) =>
                    setCellSaves((held) => ({
                      ...held,
                      [`${row.enrollment_id}/${item.id}`]: value,
                    }))
                  }
                  courseId={courseId}
                  row={row}
                  item={item}
                  version={grades.data?.updated_at}
                  refresh={refresh}
                  onDraft={(value) =>
                    setScoreDrafts((held) => {
                      const next = { ...held[row.enrollment_id] };
                      if (value === undefined) delete next[item.id];
                      else next[item.id] = value;
                      const all = { ...held };
                      if (Object.keys(next).length) all[row.enrollment_id] = next;
                      else delete all[row.enrollment_id];
                      return all;
                    })
                  }
                />
              ),
              save: cellSaves[`${row.enrollment_id}/${item.id}`] ?? {
                status: "idle" as const,
              },
            };
          }),
          {
            id: "percentage",
            read:
              percentage === null
                ? registerText.noScoreYet
                : registerPhrases.score(percentage, Boolean(local)),
          },
          {
            id: "letter",
            read: noLetter(gradeLetter) ? registerText.notYetAvailable : (gradeLetter ?? registerText.notYetAvailable),
          },
        ];
      },
      act: (row) =>
        mode === "admin" &&
        row.participation_kind === "credit" &&
        !outcomes.data?.outcomes.find(
          (outcome) => outcome.enrollment_id === row.enrollment_id,
        )?.standing_certification
          ? { does: "open", verb: registerText.override }
          : grades.data?.grade_items?.some((line) => line.linked_assignments?.length)
            ? { does: "open", verb: registerText.showCoursework }
            : undefined,
      detail: (row) => row.contributions ? (
        <div className="grid gap-2">
          {Object.values(row.contributions).flat().length === 0
            ? <p>No released coursework contributes to this result yet.</p>
            : <ul className="grid gap-2">{Object.values(row.contributions).flat().map(piece => {
              const address = `feedback:${piece.submission_id}/${piece.feedback_id}` as const;
              const label = `${piece.assignment_title} · ${piece.attempt_number == null ? "Instructor record" : `Attempt ${piece.attempt_number}`}${piece.percent === null ? "" : ` · ${piece.percent}%`}`;
              return <li key={`${piece.submission_id}/${piece.feedback_id}`}>
                {visitHost ? <button type="button" data-act="visit" data-visit-to={address} className="min-h-11 text-ui font-medium text-primary hover:underline" onClick={() => visitHost.visit(address, { box: "grade-scores" })}>{label}</button>
                  : <Link data-nav to={`${pathname}${searchWithTrail(search, [address])}`}>{label}</Link>}
              </li>;
            })}</ul>}
        </div>
      ) : (
        <>
          <p className="text-meta text-muted-foreground">Current coursework references</p>
          {(grades.data?.grade_items ?? [])
            .flatMap((line) => line.linked_assignments ?? [])
            .map((work) => (
              <Link key={work.id} data-nav to={workPath(work.id)}>
                {work.title}
              </Link>
            ))}
        </>
      ),
      task: (row) => {
        if (
          mode !== "admin" ||
          row.participation_kind !== "credit" ||
          outcomes.data?.outcomes.find(
            (outcome) => outcome.enrollment_id === row.enrollment_id,
          )?.standing_certification
        )
          return undefined;
        const draft = overrides[row.enrollment_id] ?? {
          version: grades.data?.updated_at,
          score: "",
          letter: "",
          reason: "",
        };
        const result = overrideResults[row.enrollment_id] ?? { state: "none" };
        const conflict = draft.version !== grades.data?.updated_at;
        const set = (field: keyof typeof draft, value: string) => {
          setOverrides((old) => ({
            ...old,
            [row.enrollment_id]: { ...draft, [field]: value },
          }));
          setOverrideResults((old) => ({
            ...old,
            [row.enrollment_id]: { state: "none" },
          }));
        };
        return {
          result,
          render: () => (
            <div className="grid gap-3" id={`override-${row.enrollment_id}`}>
              {conflict && <ConflictNotice object={registerText.workingFinalResult} message={registerText.theGradebookChangedYourOverrideDraftIsKept}
                facts={[{ label: registerText.currentScore, theirs: String(row.course_pct ?? registerText.notRecorded) }, { label: registerText.currentLetter, theirs: noLetter(row.letter) ? registerText.notAvailable : row.letter }]}
                keepMineLabel={registerText.keepMyOverrideOnTheNewerGradebook} loadTheirsLabel={registerText.loadTheNewerGradebook}
                onKeepMine={() => setOverrides((held) => ({ ...held, [row.enrollment_id]: { ...draft, version: grades.data?.updated_at } }))}
                onLoadTheirs={() => setOverrides((held) => { const next = { ...held }; delete next[row.enrollment_id]; return next; })} />}
              <fieldset disabled={result.state === "unknown"} className="grid gap-3">
              <Label>
                {registerText.finalScore}<Input
                  aria-label={registerText.finalScore}
                  value={draft.score}
                  onChange={(e) => set("score", e.target.value)}
                />
              </Label>
              <Label>
                {registerText.letterGrade}<Input
                  aria-label={registerText.letterGrade}
                  value={draft.letter}
                  onChange={(e) => set("letter", e.target.value)}
                />
              </Label>
              <Label>
                {registerText.overrideReason}<Textarea
                  aria-label={registerText.overrideReason}
                  value={draft.reason}
                  onChange={(e) => set("reason", e.target.value)}
                />
              </Label>
              </fieldset>
            </div>
          ),
          commit: {
            verb: registerText.recordOverride,
            cap: registerText.whenYouOverrideThisResult,
            effects: () => [
              {
                id: row.enrollment_id,
                sentence: registerPhrases.overrideEffect(draft.letter),
              },
            ],
            blockers: () => conflict ? [{ kind: "conflict", part: `override-${row.enrollment_id}`, sentence: registerText.chooseHowToUseTheNewerGradebook }] :
              draft.score.trim() &&
              Number.isFinite(Number(draft.score)) &&
              Number(draft.score) >= 0 &&
              Number(draft.score) <= 100 &&
              draft.letter.trim() &&
              draft.reason.trim() &&
              grades.data?.updated_at
                ? []
                : [
                    {
                      kind: "missing",
                      part: `override-${row.enrollment_id}`,
                      sentence:
                        registerText.enterAScoreFrom0To100ALetterAnd,
                    },
                  ],
            run: async (key) => {
              const result = await registerCommit(
                key,
                async () => {
                  try { return await gradebookApi.overrideGrade(
                    courseId,
                    {
                      enrollment_id: row.enrollment_id,
                      final_score: Number(draft.score),
                      final_grade_letter: draft.letter.trim(),
                      override_reason: draft.reason.trim(),
                    },
                    { key, version: draft.version! },
                  ); } catch (error) {
                    if (error instanceof ApiError && error.status === 409) await refresh();
                    throw error;
                  }
                },
                async () => {
                  await refresh();
                  setOverrides((held) => { const next = { ...held }; delete next[row.enrollment_id]; return next; });
                  return recorded([
                    registerText.workingFinalResultOverriddenCertificationStaysSeparate,
                  ]);
                },
              );
              return keepResult(result, (value) =>
                setOverrideResults((old) => ({
                  ...old,
                  [row.enrollment_id]: value,
                })),
              );
            },
          },
        };
      },
    },
  };
  const blockers = (): Blocker[] => {
    const reason =
      basisEditing || Object.keys(scoreDrafts).length
        ? registerText.saveOrRestoreTheEditedAssessmentFieldsAndScoresBefore
        : !items.length || weight !== 100
        ? registerCopy.weight(weight)
        : !credit.length
          ? registerText.noOpenCreditEnrollmentsToSubmit
          : credit.some(
                (row) => row.course_pct === null || noLetter(row.letter),
              )
            ? registerText.everyCreditLearnerNeedsAMappableFinalResultBeforeSubmission
            : !grades.data?.updated_at
              ? registerText.readTheLatestGradebookBeforeSubmitting
              : null;
    return reason
      ? [{ kind: "missing", part: "proposals", sentence: reason }]
      : [];
  };
  const submit = async (
    key: string,
    ids: string[],
    retry = false,
    version = grades.data!.updated_at!,
  ): Promise<Result> => {
    const next = await registerCommit<SubmitFinalResult | BulkResult>(
      key,
      () => gradebookApi.submitFinal(courseId, { key, version, retry }, ids),
      async (value) => {
        await refresh();
        const bulk =
          "bulk" in value
            ? value.bulk
            : "succeeded" in value
              ? value
              : undefined;
        return bulk
          ? bulkResult(bulk, key, () =>
              submit(
                key,
                bulk.failed.map((item) => item.id),
                true,
                version,
              ),
            )
          : recorded([
              registerText.finalGradesProposedForAdministratorCertification,
            ]);
      },
    );
    return keepResult(next, setSubmitResult);
  };
  const final: TaskBox<GradesGrid> = {
    kind: "task",
    id: "submit-final",
    label: registerText.submitFinalGrades,
    job: registerText.proposeFinalResultsForExplicitAdministratorCertification,
    owner: { system: "imam" },
    read: registerRead(grades, registerText.finalGradeProposals),
    fold: "choice",
    detail: () => registerText.proposesFinalGradesForAdministratorReview,
    state: () => ({
      class: grades.data?.locked ? "done" : "waiting",
      word: grades.data?.locked ? registerText.submitted : registerText.readyToSubmit,
    }),
    act: { does: "open", verb: registerText.submitFinalGrades },
    candidate: {
      eligible: mode === "instructor",
      // Opening the review is useful even when its commit needs repair.
      blocked: null,
      rank: null,
    },
    draft: false,
    result: submitResult,
    over: {
      selection: selectionOf(
        scores as ListBox<unknown, unknown>,
        credit.map((row) => row.enrollment_id),
        0,
      ),
      leftOut: rows
        .filter((row) => row.participation_kind === "audit")
        .map((row) => ({
          id: row.enrollment_id,
          sentence: registerCopy.leftOut(row.display_name),
        })),
    },
    parts: [
      {
        id: "proposals",
        label: registerText.finalProposals,
        state: { class: "waiting", word: registerText.notSubmitted },
        markedByBox: true,
        owner: { system: "imam" },
        fold: "never",
        draft: "none",
        render: () => null,
      },
    ],
    commit: {
      verb: registerText.submitFinalGrades,
      cap: registerText.whenYouSubmitFinalGrades,
      effects: () => [
        { id: "lock", sentence: registerText.lockTheAssessmentLinesAndEveryScore },
        ...credit.map((row) => ({
          id: row.enrollment_id,
          sentence: registerPhrases.proposalEffect(row.display_name, row.letter),
        })),
      ],
      blockers,
      run: (key) =>
        submit(
          key,
          credit.map((row) => row.enrollment_id),
        ),
    },
  };
  const boxes = [
    ...lineBoxes,
    scores as Box,
    ...(mode === "instructor" ? [final as Box] : []),
    outcomeBox,
  ].map((box) =>
    box.candidate
      ? ({
          ...box,
          candidate: registerCandidate(box.candidate, box.id, ranks.isError ? undefined : ranks.data),
        } as Box)
      : box,
  );
  return (
    <Bento
      id="gradebook"
      journey={`gradebook-${courseId}-${mode}`}
      boxes={boxes}
      onOpenChange={onLineOpenChange}
    />
  );
}
